import React, { memo } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import type { Priority } from '../../redux/tasks'
import styles from './FocusTaskCard.module.css'

const COMPLETE_ICON = 'img/habits.svg'

const iconSrc = (path: string) =>
  path.startsWith('http') || path.startsWith('/') ? path : `/${path}`

const PRIORITY_LABEL: Record<Priority, string> = {
  p1: 'P1',
  p2: 'P2',
  p3: 'P3',
  p4: 'P4',
}

const PRIORITY_CLASS: Record<Priority, string> = {
  p1: styles.priorityP1,
  p2: styles.priorityP2,
  p3: styles.priorityP3,
  p4: styles.priorityP4,
}

export type FocusTaskCardProps = {
  title: string
  metaLine?: string
  completed?: boolean
  priority?: Priority
  onToggle?: () => void
  onPomodoro?: () => void
  onViewTask?: () => void
  onDelete?: () => void
}

const ClockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.75" />
    <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
  </svg>
)

const TrashIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M9 3h6M4 7h16M10 11v6M14 11v6M6 7l1 14h10l1-14"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const FocusTaskCard = memo(function FocusTaskCard({
  title,
  metaLine,
  completed = false,
  priority,
  onToggle,
  onPomodoro,
  onViewTask,
  onDelete,
}: FocusTaskCardProps) {
  const meta = metaLine?.trim()
  const reduceMotion = useReducedMotion()
  const springTransition = reduceMotion
    ? { duration: 0 }
    : { type: 'spring' as const, stiffness: 380, damping: 28 }

  return (
    <motion.div
      layout
      className={`${styles.card} ${completed ? styles.completed : ''} ${
        priority ? styles.cardHasPriority : ''
      }`}
      initial={reduceMotion ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={reduceMotion ? undefined : { opacity: 0, scale: 0.96 }}
      whileHover={reduceMotion ? undefined : { y: -2 }}
      transition={springTransition}
    >
      {priority ? (
        <span
          className={`${styles.priorityEdge} ${PRIORITY_CLASS[priority]}`}
          role="img"
          aria-label={`${PRIORITY_LABEL[priority]} priority`}
        />
      ) : null}
      <motion.button
        type="button"
        className={`${styles.checkbox} ${completed ? styles.checkboxDone : ''}`}
        aria-pressed={completed}
        aria-label={
          completed ? `Mark incomplete: ${title}` : `Mark complete: ${title}`
        }
        onClick={onToggle}
        whileTap={reduceMotion ? undefined : { scale: 0.88 }}
        transition={{ type: 'spring', stiffness: 500, damping: 28 }}
      >
        {completed ? (
          <motion.span
            className={styles.checkboxTickMask}
            aria-hidden
            style={
              {
                WebkitMaskImage: `url("${iconSrc(COMPLETE_ICON)}")`,
                maskImage: `url("${iconSrc(COMPLETE_ICON)}")`,
              } as React.CSSProperties
            }
            initial={reduceMotion ? false : { scale: 0.4, opacity: 0 }}
            animate={reduceMotion ? undefined : { scale: 1, opacity: 1 }}
            transition={
              reduceMotion
                ? { duration: 0 }
                : { type: 'spring', stiffness: 520, damping: 22 }
            }
          />
        ) : null}
      </motion.button>
      <div className={styles.text}>
        <span className={styles.taskTitle}>{title}</span>
        {meta ? <span className={styles.meta}>{meta}</span> : null}
      </div>
      {onPomodoro || onViewTask || onDelete ? (
        <div className={styles.taskActions}>
          {onViewTask ? (
            <motion.button
              type="button"
              className={styles.viewTaskBtn}
              onClick={onViewTask}
              aria-label={`View details for ${title}`}
              whileHover={reduceMotion ? undefined : { scale: 1.05 }}
              whileTap={reduceMotion ? undefined : { scale: 0.94 }}
            >
              View Task
            </motion.button>
          ) : null}
          {onPomodoro ? (
            <motion.button
              type="button"
              className={styles.pomodoroBtn}
              onClick={onPomodoro}
              aria-label={`Start Pomodoro for ${title}`}
              whileHover={reduceMotion ? undefined : { scale: 1.05 }}
              whileTap={reduceMotion ? undefined : { scale: 0.94 }}
            >
              <ClockIcon />
            </motion.button>
          ) : null}
          {onDelete ? (
            <motion.button
              type="button"
              className={styles.deleteBtn}
              onClick={onDelete}
              aria-label={`Delete ${title}`}
              whileHover={reduceMotion ? undefined : { scale: 1.05 }}
              whileTap={reduceMotion ? undefined : { scale: 0.94 }}
            >
              <TrashIcon />
            </motion.button>
          ) : null}
        </div>
      ) : null}
    </motion.div>
  )
})

export default FocusTaskCard
